// Typed client for /opportunities + cross-entity routes.
//
// §10.6 Deal Terminal History: close/reopen/won/lost events are
// appended via POST /opportunities/{id}/events. The legacy LT-336
// terminal-history (with amend/redact) and the ``win_loss_reason``
// field are gone — see `OpportunityEvent` in `../types/pipeline.ts`.

import axios from 'axios';
import {
  Opportunity,
  OpportunityAdvance,
  OpportunityCreate,
  OpportunityEvent,
  OpportunityEventCreate,
  OpportunityStageHistoryEntry,
  OpportunityUpdate,
  OpportunityWithEvents,
} from '../types/pipeline';

const API_BASE_URL = process.env.REACT_APP_API_URL;
if (!API_BASE_URL) {
  throw new Error('REACT_APP_API_URL environment variable is required');
}

const client = axios.create({ baseURL: API_BASE_URL, timeout: 30000 });

client.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface ListOpportunitiesParams {
  pipeline_id?: string;
  stage_id?: string;
  owner_id?: string;
  include_closed?: boolean;
  limit?: number;
  offset?: number;
}

export const opportunitiesAPI = {
  list: async (params: ListOpportunitiesParams = {}): Promise<Opportunity[]> => {
    const r = await client.get<Opportunity[]>('/opportunities/', { params });
    return r.data;
  },

  get: async (id: string): Promise<OpportunityWithEvents> => {
    const r = await client.get<OpportunityWithEvents>(`/opportunities/${id}`);
    return r.data;
  },

  create: async (payload: OpportunityCreate): Promise<Opportunity> => {
    const r = await client.post<Opportunity>('/opportunities/', payload);
    return r.data;
  },

  update: async (id: string, patch: OpportunityUpdate): Promise<Opportunity> => {
    const r = await client.patch<Opportunity>(`/opportunities/${id}`, patch);
    return r.data;
  },

  remove: async (id: string): Promise<void> => {
    await client.delete(`/opportunities/${id}`);
  },

  advance: async (id: string, payload: OpportunityAdvance): Promise<Opportunity> => {
    const r = await client.post<Opportunity>(
      `/opportunities/${id}/advance`,
      payload
    );
    return r.data;
  },

  stageHistory: async (id: string): Promise<OpportunityStageHistoryEntry[]> => {
    const r = await client.get<OpportunityStageHistoryEntry[]>(
      `/opportunities/${id}/stage-history`
    );
    return r.data;
  },

  // §10.6 — Deal Terminal History (append-only events).

  events: async (id: string): Promise<OpportunityEvent[]> => {
    const r = await client.get<OpportunityEvent[]>(
      `/opportunities/${id}/events`
    );
    return r.data;
  },

  appendEvent: async (
    id: string,
    payload: OpportunityEventCreate
  ): Promise<OpportunityEvent[]> => {
    // Server returns the row(s) it just inserted. For kind='reopen'
    // this is a 2-element array (reopen first, then the synthetic
    // open in the new cycle).
    const r = await client.post<OpportunityEvent[]>(
      `/opportunities/${id}/events`,
      payload
    );
    return r.data;
  },

  listForAccount: async (
    accountId: string,
    { includeClosed = true }: { includeClosed?: boolean } = {}
  ): Promise<Opportunity[]> => {
    const r = await client.get<Opportunity[]>(
      `/accounts/${accountId}/opportunities`,
      { params: { include_closed: includeClosed } }
    );
    return r.data;
  },

  listForContact: async (
    contactId: string,
    { includeClosed = true }: { includeClosed?: boolean } = {}
  ): Promise<Opportunity[]> => {
    const r = await client.get<Opportunity[]>(
      `/contacts/${contactId}/opportunities`,
      { params: { include_closed: includeClosed } }
    );
    return r.data;
  },
};
